import { useState, useEffect } from "react";
import { api, errMsg, fileToDataUrl, dataUrlToThumb } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Camera, X } from "lucide-react";

export function PhotoInput({ photo, setPhoto, testid }) {
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { setPhoto(await fileToDataUrl(f)); } catch { toast.error("Couldn't read that photo"); }
  };
  return (
    <div>
      <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-md p-6 sm:p-4 min-h-20 sm:min-h-0 cursor-pointer hover:border-[#c8102e] transition-colors text-sm font-medium text-slate-600">
        <Camera className="w-5 h-5 text-[#c8102e]" /> {photo ? "Change photo" : "Take / upload photo (required)"}
        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid={testid} />
      </label>
      {photo && <img src={photo} alt="preview" className="mt-2 rounded-md max-h-40 object-cover" data-testid={`${testid}-preview`} />}
    </div>
  );
}

function VisitedPropertyPicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (value) return;
    const t = setTimeout(() => {
      api.get("/properties/available", { params: { q } }).then(({ data }) => setResults(data.filter((p) => p.division !== "Support")));
    }, 250);
    return () => clearTimeout(t);
  }, [q, value]);
  if (value) {
    return (
      <div className="flex items-center justify-between border-2 border-[#1a1f36] rounded-md px-3 py-2 text-sm" data-testid="visited-property-selected">
        <span className="font-medium text-[#1a1f36]">{value.name}</span>
        <button type="button" onClick={() => onChange(null)} className="text-slate-400 hover:text-slate-700" data-testid="visited-property-clear"><X className="w-4 h-4" /></button>
      </div>
    );
  }
  return (
    <div>
      <Input placeholder="Search the property you visited…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="visited-property-search" />
      <div className="mt-1 border border-slate-200 rounded-md divide-y max-h-40 overflow-auto" data-testid="visited-property-results">
        {results.map((p) => (
          <button key={p.id} type="button" onClick={() => onChange(p)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" data-testid={`visited-property-option-${p.id}`}>
            {p.name} <span className="text-xs text-slate-400">{p.location}</span>
          </button>
        ))}
        {results.length === 0 && <div className="px-3 py-3 text-xs text-slate-400 text-center">No properties match.</div>}
      </div>
    </div>
  );
}

export function SubmitTaskDialog({ task, division, onClose, onDone }) {
  const isVisit = division === "Support";
  const [form, setForm] = useState({ person_name: "", person_role: isVisit ? "support" : "office", supervisor: "" });
  const [photo, setPhoto] = useState(null);
  const [visited, setVisited] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!photo) return toast.error("A photo is required");
    if (isVisit && !visited) return toast.error("Select the property you visited");
    setBusy(true);
    try {
      const photo_thumb = await dataUrlToThumb(photo).catch(() => null);
      await api.post("/my/submissions", { task_id: task.id, ...form, photo, photo_thumb, visited_property_id: visited?.id || null });
      toast.success("Submitted for approval!");
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="p-0 gap-0 flex flex-col overflow-hidden sm:max-w-md sm:max-h-[92vh] max-sm:top-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:h-[100dvh] max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:rounded-none max-sm:border-0" data-testid="submit-task-dialog">
        <DialogHeader className="px-5 pt-5 text-left shrink-0"><DialogTitle className="font-display">{task.name}</DialogTitle></DialogHeader>
        <p className="px-5 pt-1 text-sm text-slate-600 shrink-0">{isVisit ? "15 pts to your floor (+10 to the property)" : `${task.points} pts`} · sign-off: {isVisit ? "Property supervisor" : task.supervised_by}</p>
        <form onSubmit={submit} className="flex-1 min-h-0 flex flex-col">
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            <div>
              <Label className="text-sm">Who did it?</Label>
              <Input value={form.person_name} onChange={(e) => setForm({ ...form, person_name: e.target.value })} required placeholder="Full name" data-testid="submission-person-name" />
            </div>
            <div>
              <Label className="text-sm">Their team</Label>
              <Select value={form.person_role} onValueChange={(v) => setForm({ ...form, person_role: v })}>
                <SelectTrigger data-testid="submission-person-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="office">Office / Leasing</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="support">Support Team</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isVisit && (
              <div>
                <Label className="text-sm">Property visited (on-site or by Teams)</Label>
                <VisitedPropertyPicker value={visited} onChange={setVisited} />
              </div>
            )}
            {(isVisit || task.direction === "support_team") && (
              <p className="text-xs text-slate-500 bg-slate-50 rounded-md p-2" data-testid="remote-hint">
                Remote counts — joining over a Teams call works, and a screenshot of the call is a valid photo.
              </p>
            )}
            <div>
              <Label className="text-sm">Supervisor sign-off ({isVisit ? "property supervisor" : task.supervised_by})</Label>
              <Input value={form.supervisor} onChange={(e) => setForm({ ...form, supervisor: e.target.value })} required placeholder="Supervisor's name" data-testid="submission-supervisor" />
            </div>
            <PhotoInput photo={photo} setPhoto={setPhoto} testid="submission-photo-input" />
            <p className="text-xs text-slate-500" data-testid="safety-line">Approved tasks only — if it feels unsafe, skip it. Maintenance supervises all maintenance tasks.</p>
          </div>
          <div className="shrink-0 border-t border-slate-100 bg-white px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <Button type="submit" disabled={busy} className="w-full h-12 sm:h-10 text-base sm:text-sm sw-red hover:bg-red-700 text-white font-semibold" data-testid="submission-submit-btn">{busy ? "Submitting…" : "Submit for approval"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
